import React from "react";
import { Link } from "react-router-dom";
import { Heart, Award, MapPin, Dices, PawPrint, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { OBI } from "../assets/obi";

const AboutPage = () => {
  const { t } = useLang();

  const chapters = [
    { num: "01", titleKey: "about.chapter1_title", textKey: "about.chapter1_text", img: OBI.hero, bg: "bg-pink-200" },
    { num: "02", titleKey: "about.chapter2_title", textKey: "about.chapter2_text", img: OBI.dice, bg: "bg-sky-200" },
    { num: "03", titleKey: "about.chapter3_title", textKey: "about.chapter3_text", img: OBI.shopping, bg: "bg-yellow-200" },
  ];

  const values = [
    { Icon: Award, titleKey: "about.value1_title", textKey: "about.value1_text", color: "bg-pink-400" },
    { Icon: Heart, titleKey: "about.value2_title", textKey: "about.value2_text", color: "bg-sky-400" },
    { Icon: MapPin, titleKey: "about.value3_title", textKey: "about.value3_text", color: "bg-yellow-400" },
  ];

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-pink-100 border-b-4 border-slate-900 bg-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <span className="inline-flex items-center gap-2 bg-yellow-300 border-4 border-slate-900 rounded-full px-4 py-1.5 font-bold text-sm cartoon-shadow-sm">
              <PawPrint className="w-4 h-4" /> {t("about.hero_eyebrow")}
            </span>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-slate-900">
              {t("about.hero_title")}
            </h1>
            <p className="text-xl text-slate-700 max-w-xl">
              {t("about.hero_subtitle")}
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-sky-400 rounded-full border-4 border-slate-900 cartoon-shadow hidden sm:block" />
            <div className="bg-gradient-to-br from-yellow-100 to-pink-100 border-4 border-slate-900 rounded-[2.5rem] cartoon-shadow-lg overflow-hidden">
              <img
                src={OBI.hero}
                alt="OBI il golden retriever"
                className="w-full h-full object-cover animate-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 lg:space-y-20">
          {chapters.map((c, i) => (
            <article
              key={i}
              data-testid={`about-chapter-${i + 1}`}
              className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className="lg:col-span-5 [direction:ltr]">
                <div className={`${c.bg} border-4 border-slate-900 rounded-3xl cartoon-shadow-lg overflow-hidden aspect-square`}>
                  <img src={c.img} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="lg:col-span-7 space-y-4 [direction:ltr]">
                <span className="inline-block font-display font-bold text-7xl text-pink-300 leading-none">
                  {c.num}
                </span>
                <h2 className="font-display font-bold text-3xl lg:text-4xl text-slate-900">
                  {t(c.titleKey)}
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed">
                  {t(c.textKey)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-amber-100 border-y-4 border-slate-900 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-4xl lg:text-5xl text-slate-900 text-center mb-12">
            {t("about.values_title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {values.map(({ Icon, titleKey, textKey, color }, i) => (
              <div key={i} className="cartoon-card bg-white p-6 lg:p-8">
                <div className={`w-16 h-16 ${color} border-4 border-slate-900 rounded-2xl flex items-center justify-center cartoon-shadow-sm mb-5`}>
                  <Icon className="w-8 h-8 text-slate-900" strokeWidth={2.5} />
                </div>
                <h3 className="font-display font-bold text-2xl text-slate-900 mb-2">
                  {t(titleKey)}
                </h3>
                <p className="text-slate-700">{t(textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="cartoon-card bg-sky-200 p-8 lg:p-12 text-center">
            <Dices className="w-12 h-12 mx-auto" strokeWidth={2.5} />
            <h2 className="font-display font-bold text-4xl lg:text-5xl mt-4 text-slate-900">
              {t("about.cta_title")}
            </h2>
            <p className="text-lg text-slate-700 mt-3">{t("about.cta_subtitle")}</p>
            <Link
              to="/catalog"
              data-testid="about-cta-catalog"
              className="cartoon-btn bg-yellow-300 hover:bg-yellow-200 mt-8 inline-flex text-lg py-4 px-7"
            >
              {t("about.cta_button")}
              <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
